// اموال و کالاهای سرمایه‌ای / کالاهای مصرفی
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePagedList, CountBadge } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { openReport } from '../../components/report';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

/* ── ابزارهای مشترک ── */
const BASE = '/api/tadarokat/property';

const API = (path, opts = {}) => fetch(`${BASE}${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const qs = (o) => Object.entries(o || {})
  .filter(([, v]) => v !== undefined && v !== null && v !== '')
  .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s ?? '').replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
const fmtAmt = n => Number(n || 0).toLocaleString('fa-IR');
const curYear = () => {
  const y = new Date().toLocaleDateString('fa-IR-u-nu-latn').split('/')[0];
  return parseInt(y, 10) || 1404;
};

const isEmpty = v => v === null || v === undefined || v === '' || v === 0 || v === '0';
const isNum = v => typeof v === 'number';
const readSummary = (k) => { try { return localStorage.getItem(k) === '1'; } catch { return false; } };
const saveSummary = (k, v) => { try { localStorage.setItem(k, v ? '1' : '0'); } catch { /* */ } };

/* ── ستون‌ها ── */
function visibleColumns(columns, rows) {
  if (!rows || rows.length === 0) return columns || [];
  return (columns || []).filter(c => rows.some(r => !isEmpty(r[c])));
}
const excelCols = (columns, rows) =>
  visibleColumns(columns, rows).map(c => ({ key: c, label: c, num: rows.some(r => isNum(r[c])) }));

/* ── نوع مال ── */
const TYPE_ITEMS = [
  { v: 0, l: 'مصرفی و در حکم مصرفی' }, { v: 1, l: 'اثاثه و لوازم برقی' }, { v: 2, l: 'اثاثه و لوازم غیربرقی' },
  { v: 3, l: 'لوازم و تجهیزات رایانه' }, { v: 4, l: 'وسایط نقلیه موتوری' }, { v: 5, l: 'ابزار و ماشین آلات' },
  { v: 6, l: 'فرش دستبافت' }, { v: 7, l: 'ارتباطی، صوتی، تصویری' }, { v: 8, l: 'دوربینها و تجهیزات آن' },
  { v: 9, l: 'کتب' }, { v: 10, l: 'ورزشی موتوری/برقی' }, { v: 11, l: 'لوح فشرده' }, { v: 12, l: 'گروه ورزشی' },
  { v: 13, l: 'ساختمان و مستحدثات' }, { v: 14, l: 'بی مهره ها (تاکسیدرمی)' }, { v: 15, l: 'اموال فرهنگی، تاریخی' },
  { v: 16, l: 'فرش ماشینی' }, { v: 17, l: 'مهره داران (تاکسیدرمی)' }, { v: 18, l: 'جانوران' }, { v: 19, l: 'اشیاء و لوازم نفیس' },
];

const OWNERSHIP_ITEMS = [
  { v: 1, l: 'خریداری' }, { v: 2, l: 'اهدایی' }, { v: 3, l: 'تملیکی' }, { v: 4, l: 'رسیده از شرکت دولتی' },
];

const CONSUME_ITEMS = [
  { v: 0, l: 'مصرف شده' }, { v: 1, l: 'قابل واگذاری' },
];

/* ── فیلترهای فهرست (فقط variant=1) ── */
const FILTERS_V1 = [
  { v: 'all', l: 'همه' }, { v: 'consumable', l: 'مصرفی و در حکم مصرفی' },
  { v: 'others', l: 'غیرمصرفی (اموالی)' }, { v: 'absorbed', l: 'استهلاک' },
];

/* ── فرم خالی ── */
const EMPTY = () => ({
  ownership_id: 1, stock_slip_no: 0, stock_slip_date: '', label_no: 0,
  old_label_no: '', send_letter_no: '', send_letter_date: '',
  from_serial: 0, to_serial: 0,
  goods_id: 0, goods_title: '', feature_id: 0, feature_title: '',
  amount_receiver_id: 0, receiver_title: '',
  factor_no: '', factor_date: '', price: 0, added_tax: 0, discount: 0, quantity: 1,
  property_type: 0, consume_situation: 0,
  remark: '', uid1: '', uid2: '', uid3: '', uid4: '', desc1: '', desc2: '', desc3: '', desc4: '',
  life_time: 0, first_use_date: '',
  car_repair_pieces_id: 0, goods_request_detail_id: 0,
});

/* ── تبدیل ردیف فهرست به فرم ── */
function rowToForm(r) {
  return {
    ownership_id: r.OwnershipId || 1,
    stock_slip_no: r['شماره قبض'] || 0, stock_slip_date: r['تاریخ قبض'] || '',
    label_no: r['برچسب اموال'] || 0, old_label_no: r['برچسب قبلی'] || '',
    send_letter_no: r['شماره نامه'] || '', send_letter_date: r['تاریخ نامه'] || '',
    from_serial: r['از شماره سریال'] || 0, to_serial: r['تا شماره سریال'] || 0,
    goods_id: r.GoodsId || 0, goods_title: r['نام کالا'] || '',
    feature_id: r.FeatureId || 0, feature_title: r['ویژگی'] || '',
    amount_receiver_id: r.AmountReceiverId || 0, receiver_title: r['فروشنده'] || '',
    factor_no: r['شماره فاکتور'] || '', factor_date: r['تاریخ فاکتور'] || '',
    price: r['قیمت'] || 0, added_tax: r['مالیات بر ارزش افزوده'] || 0,
    discount: r['تخفیف'] || 0, quantity: r['تعداد'] || 1,
    property_type: r.PropertyType ?? 0, consume_situation: r.ConsumeSituation ?? 0,
    remark: r['توضیحات'] || '',
    uid1: r['ویژگی 1'] || '', uid2: r['ویژگی 2'] || '', uid3: r['ویژگی 3'] || '', uid4: r['ویژگی 4'] || '',
    desc1: r['ویژگی 5'] || '', desc2: r['ویژگی 6'] || '', desc3: r['ویژگی 7'] || '', desc4: r['ویژگی 8'] || '',
    life_time: r.LifeTime || 0, first_use_date: r.FirstUseDate || '',
    car_repair_pieces_id: r.CarRepairPiecesId || 0,
    goods_request_detail_id: r.GoodsRequestDetailId || 0,
  };
}

/* ── جستجوی انتخابی ── */
function Lookup({ prefix, path, value, title, onChange, disabled, placeholder = 'جستجو...' }) {
  const [text, setText] = useState(title || '');
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setText(title || ''); }, [title]);
  const search = (q) => {
    setText(q);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const j = await API(`/${prefix}/${path}?q=${encodeURIComponent(q)}`);
      setList(j.ok ? j.data : []);
      setOpen(true);
    }, 300);
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '4px' }}>
        <input value={text} disabled={disabled} placeholder={placeholder}
          onChange={e => search(e.target.value)} onFocus={() => !disabled && search(text)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          style={{ ...ui.input, background: disabled ? '#f3f3f3' : '#fff' }} />
        {!disabled && value ? <button type="button" onClick={() => { onChange(0, ''); setText(''); }}
          style={{ ...ui.btnCancel, padding: '4px 10px' }}>✕</button> : null}
      </div>
      {open && list.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 50, background: '#fff', border: '1px solid #ccc', borderRadius: '8px',
          maxHeight: '240px', overflowY: 'auto', width: '100%', boxShadow: '0 6px 18px rgba(0,0,0,0.15)' }}>
          {list.map(o => (
            <div key={o.id} onMouseDown={() => { onChange(o.id, o.title || o.display); setText(o.title || o.display); setOpen(false); }}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}>
              {toFa(o.display || o.title)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── فیلد ── */
function Field({ label, children, flex = 1, width }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: width ? 'none' : flex, width, minWidth: '120px' }}>
      <label style={ui.mlabel}>{label}</label>
      {children}
    </div>
  );
}
function TextIn({ value, onChange, disabled, num, ...rest }) {
  return <input value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value)}
    style={{ ...ui.input, ...(num ? { direction: 'ltr', textAlign: 'center' } : {}), background: disabled ? '#f3f3f3' : '#fff' }} {...rest} />;
}
function DateIn({ value, onChange, disabled }) {
  return <JalaliDatePicker value={value || ''} onChange={v => onChange(v)} disabled={disabled} />;
}

/* ── جدول ── */
function Grid({ columns, rows, rowKey, selectedKey, onSelect, onDoubleClick, hideEmpty = true, empty = 'رکوردی یافت نشد', moneyCols = [] }) {
  const cols = hideEmpty ? visibleColumns(columns, rows) : (columns || []);
  return (
    <>
      <table style={ui.table}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
          <tr>{cols.map(c => <th key={c} style={ui.th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => {
            const k = rowKey ? r[rowKey] : i;
            const sel = selectedKey !== undefined && selectedKey !== null && k === selectedKey;
            const bg = sel ? '#e8f0fe' : (i % 2 ? '#fdf6ec' : '#fff');
            return (
              <tr key={k ?? i} style={{ background: bg, cursor: onSelect ? 'pointer' : 'default', outline: sel ? '2px solid #1a2f50' : 'none' }}
                onClick={() => onSelect && onSelect(r)} onDoubleClick={() => onDoubleClick && onDoubleClick(r)}>
                {cols.map(c => {
                  const v = r[c];
                  const money = moneyCols.includes(c);
                  const txt = isNum(v) ? (money ? fmtAmt(v) : toFa(v)) : (v ?? '');
                  return <td key={c} style={{ ...(isNum(v) ? ui.tdNum : ui.td), whiteSpace: 'pre-line' }}>{txt}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {(!rows || rows.length === 0) && <div style={ui.empty}>{empty}</div>}
    </>
  );
}

/* ── مودال ── */
function Modal({ title, onClose, children, footer, width = '820px' }) {
  return (
    <div style={ui.overlay}>
      <div style={{ ...ui.modal, maxWidth: width }}>
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>{title}</div>
          <button onClick={onClose} style={ui.closeBtn}>✕</button>
        </div>
        <div style={ui.mBody}>{children}</div>
        {footer && <div style={ui.mFooter}>{footer}</div>}
      </div>
    </div>
  );
}

function DeleteDialog({ onConfirm, onCancel }) {
  return <WarningDialog title="هشدار" lines={['آيا از حذف اين رکورد مطمئن هستيد؟']} confirmLabel="بلی" cancelLabel="خیر"
    tone="danger" onConfirm={onConfirm} onCancel={onCancel} />;
}

function MsgDialog({ text, onClose }) {
  return <WarningDialog title="پیام" lines={String(text || '').split('\n').filter(Boolean)} confirmLabel="تایید" cancelLabel="بستن"
    onConfirm={onClose} onCancel={onClose} />;
}

/* ══════════════════════════════════════════════════════════════════
   راهنمای ماژول اموال
   ══════════════════════════════════════════════════════════════════ */
const helpSectionStyle = { marginBottom: '18px' };
const helpH3 = { fontSize: '14px', fontWeight: 700, color: '#1a237e', marginBottom: '6px', borderBottom: '1px solid #e0e0e0', paddingBottom: '4px' };
const helpTable = { width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '8px' };
const helpTh = { textAlign: 'right', background: '#f5f5f5', padding: '5px 8px', border: '1px solid #e0e0e0', fontWeight: 700 };
const helpTd = { textAlign: 'right', padding: '5px 8px', border: '1px solid #e0e0e0' };
const helpUl = { margin: '4px 0 0 0', paddingRight: '18px', fontSize: '12px', lineHeight: 1.8 };

function HelpDialog({ variant, onClose }) {
  const v = variant;
  return (
    <Modal title="❓ راهنمای ماژول اموال" onClose={onClose} width="750px">
      <div style={{ direction: 'rtl', fontFamily: 'inherit', lineHeight: 1.9 }}>

        {/* ورود */}
        <div style={helpSectionStyle}>
          <h3 style={helpH3}>ورود به ماژول</h3>
          <table style={helpTable}>
            <thead><tr><th style={helpTh}>حالت</th><th style={helpTh}>فرم مجوز</th><th style={helpTh}>توضیح</th></tr></thead>
            <tbody>
              <tr><td style={helpTd}>اموال سرمایه‌ای</td><td style={helpTd}>FRMPROPERTY</td><td style={helpTd}>شامل استهلاک و ارسال به خزانه</td></tr>
              <tr><td style={helpTd}>کالاهای مصرفی</td><td style={helpTd}>FRMPROPERTY2</td><td style={helpTd}>بدون استهلاک و خزانه</td></tr>
            </tbody>
          </table>
        </div>

        {/* فیلترها */}
        <div style={helpSectionStyle}>
          <h3 style={helpH3}>فیلترهای فهرست</h3>
          <table style={helpTable}>
            <thead><tr><th style={helpTh}>فیلتر</th><th style={helpTh}>عملکرد</th></tr></thead>
            <tbody>
              <tr><td style={helpTd}>سال مالی</td><td style={helpTd}>انتخاب سال ثبت اموال</td></tr>
              {v === 1 && <tr><td style={helpTd}>فیلتر</td><td style={helpTd}>همه / مصرفی و در حکم مصرفی / اموال سرمایه‌ای</td></tr>}
              <tr><td style={helpTd}>نوع مال</td><td style={helpTd}>انتخاب از ۲۰ دسته‌بندی (اثاثه، رایانه، نقلیه و...)</td></tr>
              <tr><td style={helpTd}>دارای موجودی</td><td style={helpTd}>فقط اقلامی که تعداد بیشتر از صفر دارند</td></tr>
              <tr><td style={helpTd}>جستجو</td><td style={helpTd}>جستجو در نام کالا و توضیحات — Enter بزنید</td></tr>
              <tr><td style={helpTd}>فقط برچسب</td><td style={helpTd}>جستجو فقط در شماره برچسب</td></tr>
              <tr><td style={helpTd}>شماره/سال قبض</td><td style={helpTd}>فیلتر بر اساس قبض انبار</td></tr>
            </tbody>
          </table>
        </div>

        {/* دکمه‌ها */}
        <div style={helpSectionStyle}>
          <h3 style={helpH3}>ثبت و ویرایش</h3>
          <table style={helpTable}>
            <thead><tr><th style={helpTh}>دکمه</th><th style={helpTh}>عملکرد</th></tr></thead>
            <tbody>
              <tr><td style={helpTd}>＋ جدید / قبض جدید</td><td style={helpTd}>ثبت قلم اموال جدید یا ایجاد قبض انبار جدید</td></tr>
              <tr><td style={helpTd}>✎ ویرایش</td><td style={helpTd}>ویرایش قلم انتخاب‌شده</td></tr>
              <tr><td style={helpTd}>✕ حذف</td><td style={helpTd}>حذف قلم انتخاب‌شده با تایید</td></tr>
              <tr><td style={helpTd}>حذف گروهی</td><td style={helpTd}>حذف تمام اقلام نمایش‌داده‌شده</td></tr>
            </tbody>
          </table>
        </div>

        {/* فیلدهای فرم */}
        <div style={helpSectionStyle}>
          <h3 style={helpH3}>فیلدهای فرم ثبت</h3>
          <ul style={helpUl}>
            <li><b>مالکیت:</b> خریداری / اهدایی / تملیکی / رسیده از شرکت دولتی</li>
            <li><b>شماره و تاریخ قبض:</b> اطلاعات قبض انبار</li>
            <li><b>برچسب اموال:</b> شماره برچسب منحصربه‌فرد</li>
            <li><b>کالا:</b> جستجو و انتخاب از فهرست کالاها</li>
            <li><b>ویژگی:</b> انتخاب ویژگی کالا یا ایجاد ویژگی جدید</li>
            <li><b>فروشنده:</b> جستجو و انتخاب طرف حساب</li>
            <li><b>قیمت / مالیات / تخفیف / تعداد:</b> اطلاعات مالی</li>
            <li><b>نوع مال:</b> انتخاب از ۲۰ دسته‌بندی</li>
            {v === 1 && <li><b>عمر مفید / تاریخ بهره‌برداری:</b> برای محاسبه استهلاک</li>}
          </ul>
        </div>

        {/* ابزارها */}
        <div style={helpSectionStyle}>
          <h3 style={helpH3}>ابزارهای ویژه</h3>
          <table style={helpTable}>
            <thead><tr><th style={helpTh}>ابزار</th><th style={helpTh}>توضیح</th></tr></thead>
            <tbody>
              <tr><td style={helpTd}>📥 دریافت اموال</td><td style={helpTd}>بارگذاری گروهی از فایل اکسل در ۴ مرحله</td></tr>
              <tr><td style={helpTd}>📦 مدیریت محل</td><td style={helpTd}>تعریف و ویرایش محل‌های استقرار اموال</td></tr>
              <tr><td style={helpTd}>📍 گزارش محل</td><td style={helpTd}>لیست اموال به تفکیک محل استقرار</td></tr>
              <tr><td style={helpTd}>🧾 قبض انبار</td><td style={helpTd}>چاپ قبض رسمی انبار برای قلم انتخاب‌شده</td></tr>
              <tr><td style={helpTd}>درخواست‌ها</td><td style={helpTd}>مشاهده و ثبت درخواست‌های خرید/تعمیر</td></tr>
              <tr><td style={helpTd}>سابقه</td><td style={helpTd}>تاریخچه واگذاری قلم اموال</td></tr>
              <tr><td style={helpTd}>لاگ</td><td style={helpTd}>ثبت تغییرات (چه کسی، چه زمانی، چه تغییری)</td></tr>
              {v === 1 && <tr><td style={helpTd}>استهلاک</td><td style={helpTd}>محاسبه استهلاک بر اساس عمر مفید</td></tr>}
              {v === 1 && <tr><td style={helpTd}>ارسال به خزانه</td><td style={helpTd}>ارسال اموال اسقاطی به واحد خزانه‌داری</td></tr>}
              <tr><td style={helpTd}>گزارش کلی</td><td style={helpTd}>گزارش‌ساز پیشرفته با ذخیره قالب</td></tr>
              <tr><td style={helpTd}>خروجی اکسل</td><td style={helpTd}>تبدیل اقلام فیلترشده به فایل Excel</td></tr>
            </tbody>
          </table>
        </div>

        {/* سطح دسترسی */}
        <div style={helpSectionStyle}>
          <h3 style={helpH3}>سطح دسترسی</h3>
          <table style={helpTable}>
            <thead><tr><th style={helpTh}>مجوز</th><th style={helpTh}>دسترسی</th></tr></thead>
            <tbody>
              <tr><td style={helpTd}>FRMPROPERTY</td><td style={helpTd}>اموال سرمایه‌ای — ثبت، ویرایش، حذف، استهلاک، خزانه</td></tr>
              <tr><td style={helpTd}>FRMPROPERTY2</td><td style={helpTd}>کالاهای مصرفی — ثبت، ویرایش، حذف</td></tr>
              <tr><td style={helpTd}>FRMPROPERTYPLACE</td><td style={helpTd}>مدیریت محل اموال</td></tr>
              <tr><td style={helpTd}>FRMGETPROPERTY</td><td style={helpTd}>دریافت اموال از فایل اکسل</td></tr>
            </tbody>
          </table>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>دکمه‌هایی که مجوز ندارید به صورت خودکار مخفی می‌شوند.</p>
        </div>

        {/* نکات */}
        <div style={helpSectionStyle}>
          <h3 style={helpH3}>نکات مهم</h3>
          <ul style={helpUl}>
            <li><b>Enter</b> را بزنید تا لیست بازخوانی شود</li>
            <li>قبل از ذخیره، حتماً <b>کالا</b> را انتخاب کنید</li>
            <li>حذف همیشه با پیام تایید همراه است</li>
            <li>فیلترها با هم ترکیب می‌شوند — مثلاً نوع مال + دارای موجودی</li>
            <li>قالب گزارش‌های پرکاربرد را ذخیره کنید تا هر بار تنظیم نکنید</li>
            {v === 1 && <li>اموال مصرفی دکمه‌های استهلاک و خزانه را ندارد</li>}
          </ul>
        </div>
      </div>
    </Modal>
  );
}

const smallBtn = (bg) => ({
  padding: '6px 12px', background: bg || 'linear-gradient(135deg,#1a237e,#283593)', color: '#fff', border: 'none',
  borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
});

/* ══════════════════════════════════════════════════════════════════
   فرم ثبت/ویرایش اموال
   ══════════════════════════════════════════════════════════════════ */
function PropertyForm({ form, setF, variant, prefix, goodsLocked, newFeature }) {
  const v = variant;
  return (
    <>
      {/* ردیف ۱: قبض انبار */}
      <div style={ui.row}>
        <Field label="نحوه تملک" width="160px">
          <select value={form.ownership_id} onChange={e => setF('ownership_id', Number(e.target.value))} style={ui.input}>
            {OWNERSHIP_ITEMS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <Field label="شماره قبض انبار" width="110px">
          <TextIn num value={form.stock_slip_no} onChange={v2 => setF('stock_slip_no', Number(toEng(v2)) || 0)} />
        </Field>
        <Field label="تاریخ قبض انبار" width="140px">
          <DateIn value={form.stock_slip_date} onChange={v2 => setF('stock_slip_date', v2)} />
        </Field>
        <Field label="برچسب اموال" width="110px">
          <TextIn num value={form.label_no} onChange={v2 => setF('label_no', Number(toEng(v2)) || 0)} />
        </Field>
        <Field label="برچسب قبلی">
          <TextIn value={form.old_label_no} onChange={v2 => setF('old_label_no', v2)} />
        </Field>
      </div>
      {/* ردیف ۲: شماره نامه */}
      <div style={ui.row}>
        <Field label="شماره نامه">
          <TextIn value={form.send_letter_no} onChange={v2 => setF('send_letter_no', v2)} />
        </Field>
        <Field label="تاریخ نامه">
          <DateIn value={form.send_letter_date} onChange={v2 => setF('send_letter_date', v2)} />
        </Field>
        <Field label="از شماره سریال" width="110px">
          <TextIn num value={form.from_serial} onChange={v2 => setF('from_serial', Number(toEng(v2)) || 0)} />
        </Field>
        <Field label="تا شماره سریال" width="110px">
          <TextIn num value={form.to_serial} onChange={v2 => setF('to_serial', Number(toEng(v2)) || 0)} />
        </Field>
      </div>
      {/* ردیف ۳: کالا و ویژگی */}
      <div style={ui.row}>
        <Field label="نام کالا" flex={2}>
          <Lookup prefix={prefix} path="lookup/goods/" value={form.goods_id} title={form.goods_title} disabled={goodsLocked}
            onChange={(id, t) => setF('goods_id', id, { goods_title: t, feature_id: 0, feature_title: '' })} placeholder="جستجوی کالا..." />
        </Field>
        <Field label="ویژگی" flex={2}>
          <div style={{ display: 'flex', gap: '4px' }}>
            <div style={{ flex: 1 }}>
              <Lookup prefix={prefix} path={`lookup/feature/?goods_id=${form.goods_id}`} value={form.feature_id} title={form.feature_title}
                onChange={(id, t) => setF('feature_id', id, { feature_title: t })} placeholder="جستجوی ویژگی..." />
            </div>
            {form.goods_id > 0 && (
              <button type="button" onClick={newFeature} style={{ ...smallBtn('#00695c'), padding: '4px 8px', fontSize: '11px' }}>＋</button>
            )}
          </div>
        </Field>
      </div>
      {/* ردیف ۴: فروشنده و فاکتور */}
      <div style={ui.row}>
        <Field label="فروشنده" flex={2}>
          <Lookup prefix={prefix} path="lookup/receiver/" value={form.amount_receiver_id} title={form.receiver_title}
            onChange={(id, t) => setF('amount_receiver_id', id, { receiver_title: t })} placeholder="جستجوی فروشنده..." />
        </Field>
        <Field label="شماره فاکتور">
          <TextIn value={form.factor_no} onChange={v2 => setF('factor_no', v2)} />
        </Field>
        <Field label="تاریخ فاکتور">
          <DateIn value={form.factor_date} onChange={v2 => setF('factor_date', v2)} />
        </Field>
      </div>
      {/* ردیف ۵: مبالغ */}
      <div style={ui.row}>
        <Field label="قیمت واحد (ریال)">
          <TextIn num value={form.price} onChange={v2 => setF('price', Number(toEng(v2)) || 0)} />
        </Field>
        <Field label="مالیات ارزش افزوده">
          <TextIn num value={form.added_tax} onChange={v2 => setF('added_tax', Number(toEng(v2)) || 0)} />
        </Field>
        {v === 1 && (
          <Field label="تخفیف">
            <TextIn num value={form.discount} onChange={v2 => setF('discount', Number(toEng(v2)) || 0)} />
          </Field>
        )}
        <Field label="تعداد" width="80px">
          <TextIn num value={form.quantity} onChange={v2 => setF('quantity', Number(toEng(v2)) || 1)} />
        </Field>
      </div>
      {/* ردیف ۶: نوع و وضعیت */}
      <div style={ui.row}>
        <Field label="نوع مال">
          <select value={form.property_type} onChange={e => setF('property_type', Number(e.target.value))} style={ui.input}>
            {TYPE_ITEMS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <Field label="نوع استفاده">
          <select value={form.consume_situation} onChange={e => setF('consume_situation', Number(e.target.value))} style={ui.input}>
            {CONSUME_ITEMS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <Field label="عمر مفید (سال)" width="100px">
          <TextIn num value={form.life_time} onChange={v2 => setF('life_time', Number(toEng(v2)) || 0)} />
        </Field>
        <Field label="تاریخ بهره‌برداری">
          <DateIn value={form.first_use_date} onChange={v2 => setF('first_use_date', v2)} />
        </Field>
      </div>
      {/* ردیف ۷: توضیحات */}
      <div style={ui.row}>
        <Field label="توضیحات" flex={3}>
          <TextIn value={form.remark} onChange={v2 => setF('remark', v2)} />
        </Field>
      </div>
      {/* ردیف ۸: ویژگی‌های ۱ تا ۸ */}
      <div style={ui.row}>
        <Field label="ویژگی ۱"><TextIn value={form.uid1} onChange={v2 => setF('uid1', v2)} /></Field>
        <Field label="ویژگی ۲"><TextIn value={form.uid2} onChange={v2 => setF('uid2', v2)} /></Field>
        <Field label="ویژگی ۳"><TextIn value={form.uid3} onChange={v2 => setF('uid3', v2)} /></Field>
        <Field label="ویژگی ۴"><TextIn value={form.uid4} onChange={v2 => setF('uid4', v2)} /></Field>
      </div>
      <div style={ui.row}>
        <Field label="ویژگی ۵"><TextIn value={form.desc1} onChange={v2 => setF('desc1', v2)} /></Field>
        <Field label="ویژگی ۶"><TextIn value={form.desc2} onChange={v2 => setF('desc2', v2)} /></Field>
        <Field label="ویژگی ۷"><TextIn value={form.desc3} onChange={v2 => setF('desc3', v2)} /></Field>
        <Field label="ویژگی ۸"><TextIn value={form.desc4} onChange={v2 => setF('desc4', v2)} /></Field>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════
   دیالوگ جزئیات ردیف انتخابی
   ══════════════════════════════════════════════════════════════════ */
function DetailPanel({ data, variant }) {
  if (!data) return null;
  const { sum_slip, places, in: qIn, out: qOut, stock } = data;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px' }}>
      <div style={{ display: 'flex', gap: '16px', fontSize: '12px', flexWrap: 'wrap' }}>
        <span><b>جمع قبض:</b> {fmtAmt(sum_slip)} ریال</span>
        <span><b>ورودی:</b> {toFa(qIn)}</span>
        <span><b>خروجی:</b> {toFa(qOut)}</span>
        <span><b>موجودی:</b> {toFa(stock)}</span>
      </div>
      {places && places.rows && places.rows.length > 0 && (
        <div style={{ maxHeight: '220px', overflow: 'auto' }}>
          <Grid columns={places.columns} rows={places.rows} hideEmpty={false} empty="" />
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   دیالوگ درخواست‌های ثبت‌نشده
   ══════════════════════════════════════════════════════════════════ */
function RequestsDialog({ prefix, variant, onClose, onAdd }) {
  const [ptype, setPtype] = useState(0);
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    const j = await API(`/${prefix}/requests/?type=${t}`);
    if (j.ok) { setCols(j.columns || []); setRows(j.data || []); }
    setLoading(false);
  }, [prefix]);

  useEffect(() => { load(ptype); }, [load, ptype]);

  const toggleSel = (r) => {
    const k = r.GoodsRequestDetailId || r.id;
    setSelected(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);
  };
  const toggleAll = () => {
    if (selected.length === rows.length) setSelected([]);
    else setSelected(rows.map(r => r.GoodsRequestDetailId || r.id));
  };

  return (
    <Modal title="درخواست‌های ثبت‌نشده" onClose={onClose} width="900px"
      footer={<>
        <button onClick={onClose} style={ui.btnCancel}>بستن</button>
        <button disabled={selected.length === 0} onClick={() => onAdd(selected, 'multi')}
          style={{ ...ui.btnSave, opacity: selected.length === 0 ? 0.4 : 1 }}>ثبت همه ({toFa(selected.length)})</button>
        {selected.length === 1 && (
          <button onClick={() => onAdd(selected, 'single')}
            style={{ ...ui.btnSave, background: '#00695c' }}>ثبت تکی</button>
        )}
      </>}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Field label="نوع مال" width="200px">
          <select value={ptype} onChange={e => { setPtype(Number(e.target.value)); setSelected([]); }} style={ui.input}>
            <option value={0}>همه</option>
            {TYPE_ITEMS.map(o => <option key={o.v} value={o.v + 1}>{o.l}</option>)}
          </select>
        </Field>
        <button onClick={toggleAll} style={smallBtn('#6a1b9a')}>
          {selected.length === rows.length ? 'لغو انتخاب' : 'انتخاب همه'}
        </button>
      </div>
      {loading ? <div style={ui.empty}>در حال بارگذاری...</div> : (
        <div style={{ maxHeight: '400px', overflow: 'auto' }}>
          <table style={ui.table}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
              <tr>
                <th style={ui.th}>✓</th>
                {(cols || []).map(c => <th key={c} style={ui.th}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const k = r.GoodsRequestDetailId || r.id || i;
                const on = selected.includes(k);
                return (
                  <tr key={k} style={{ background: on ? '#e8f0fe' : (i % 2 ? '#fdf6ec' : '#fff'), cursor: 'pointer' }}
                    onClick={() => toggleSel(r)}>
                    <td style={ui.td}><input type="checkbox" checked={on} readOnly /></td>
                    {(cols || []).map(c => <td key={c} style={isNum(r[c]) ? ui.tdNum : ui.td}>{isNum(r[c]) ? toFa(r[c]) : (r[c] ?? '')}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && <div style={ui.empty}>درخواستی یافت نشد</div>}
        </div>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════
   دیالوگ سابقه (واگذاری‌ها)
   ══════════════════════════════════════════════════════════════════ */
function HistoryDialog({ prefix, pid, onClose }) {
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    API(`/${prefix}/${pid}/history/`).then(j => { if (j.ok) { setCols(j.columns || []); setRows(j.data || []); } });
  }, [prefix, pid]);
  return (
    <Modal title="سابقه واگذاری" onClose={onClose} width="800px"
      footer={<button onClick={onClose} style={ui.btnCancel}>بستن</button>}>
      <div style={{ maxHeight: '400px', overflow: 'auto' }}>
        <Grid columns={cols} rows={rows} empty="سابقه‌ای یافت نشد" />
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════
   دیالوگ لاگ تغییرات
   ══════════════════════════════════════════════════════════════════ */
function LogDialog({ prefix, pid, onClose }) {
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    API(`/${prefix}/${pid}/log/`).then(j => { if (j.ok) { setCols(j.columns || []); setRows(j.data || []); } });
  }, [prefix, pid]);
  return (
    <Modal title="سابقه تغییرات" onClose={onClose} width="800px"
      footer={<button onClick={onClose} style={ui.btnCancel}>بستن</button>}>
      <div style={{ maxHeight: '400px', overflow: 'auto' }}>
        <Grid columns={cols} rows={rows} empty="سابقه‌ای یافت نشد" />
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════
   دیالوگ استهلاک (فقط variant=1)
   — دو جدول: «به تفکیک نوع» و «به تفکیک کالا»
   — فیلتر نوع مال، موجودی، اکسل خروجی، ردیف جمع
   ══════════════════════════════════════════════════════════════════ */
function depTotalsRow(cols, rows) {
  if (!rows || rows.length === 0) return null;
  const tot = {};
  cols.forEach(c => { tot[c] = ''; });
  tot[cols[0]] = ''; tot['نوع کالا'] = 'جمع کل';
  const numCols = cols.filter(c => c !== 'PropertyType' && c !== 'نوع کالا' && rows.some(r => isNum(r[c])));
  numCols.forEach(c => { tot[c] = rows.reduce((s, r) => s + (Number(r[c]) || 0), 0); });
  return tot;
}

function DepreciationDialog({ prefix, year, onClose }) {
  const [data, setData] = useState(null);
  const [ptypeF, setPtypeF] = useState(0);
  const [inStockF, setInStockF] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('type'); // 'type' | 'goods'

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    const j = await API(`/${prefix}/depreciation/?${qs({ year, ptype: ptypeF || undefined, in_stock: inStockF ? 1 : undefined })}`);
    setLoading(false);
    if (j.ok) setData(j);
    else setErr(j.msg || 'خطا');
  }, [prefix, year, ptypeF, inStockF]);

  useEffect(() => { load(); }, [load]);

  const depMoneyAll = [
    'جمع بها', 'استهلاک سالانه', 'استهلاک سالهای قبل', 'استهلاک سالجاری', 'جمع استهلاک', 'مانده',
    'خرید سالهای قبل', 'استهلاک انباشته ابتدای دوره',
    'بهای تمام شده مبلغ تجدید ارزیابی شده', 'استهلاک انباشته پایان دوره', 'ارزش دفتری پایان دوره',
    'استهلاک سال جاری',
  ];
  // dynamic year columns are also money
  const isMoney = (c) => depMoneyAll.includes(c) || /^(خرید|استهلاک) سال \d+/.test(c);

  const typeTable = data?.by_type;
  const goodsTable = data?.by_goods;

  const typeCols = typeTable?.columns?.filter(c => c !== 'PropertyType') || [];
  const goodsCols = goodsTable?.columns?.filter(c => c !== 'PropertyType') || [];

  const typeMoneyC = typeCols.filter(isMoney);
  const goodsMoneyC = goodsCols.filter(isMoney);

  // totals row
  const typeTot = typeTable ? depTotalsRow(typeCols, typeTable.rows) : null;
  const typeRowsWithTot = typeTable ? [...(typeTable.rows || []), ...(typeTot ? [typeTot] : [])] : [];

  const tabStyle = (t) => ({
    padding: '6px 16px', border: 'none', borderBottom: tab === t ? '3px solid #1a237e' : '3px solid transparent',
    background: tab === t ? '#e8eaf6' : 'transparent', cursor: 'pointer', fontFamily: 'inherit',
    fontSize: '12px', fontWeight: tab === t ? 800 : 500, color: tab === t ? '#1a237e' : '#666',
  });

  return (
    <Modal title={`استهلاک — سال ${toFa(year)}`} onClose={onClose} width="98vw"
      footer={<>
        <button onClick={onClose} style={ui.btnCancel}>بستن</button>
        {data && (
          <button onClick={async () => {
            try { await openReport('tadarokat.property_depreciation', { year, by: tab === 'goods' ? 'goods' : 'type', ptype: ptypeF, in_stock: inStockF ? 1 : 0 }); }
            catch (e) { setErr(e.message); }
          }} style={smallBtn('#4a148c')}>🖨️ چاپ</button>
        )}
        {tab === 'type' && typeTable && (
          <ExcelButton title={`استهلاک به تفکیک نوع - ${toFa(year)}`} columns={excelCols(typeCols, typeRowsWithTot)} rows={typeRowsWithTot} />
        )}
        {tab === 'goods' && goodsTable && (
          <ExcelButton title={`استهلاک به تفکیک کالا - ${toFa(year)}`} columns={excelCols(goodsCols, goodsTable.rows)} rows={goodsTable.rows} />
        )}
      </>}>
      {/* فیلترها */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Field label="نوع مال" width="200px">
          <select value={ptypeF} onChange={e => { setPtypeF(Number(e.target.value)); }} style={ui.input}>
            <option value={0}>همه</option>
            {TYPE_ITEMS.filter(o => o.v > 0).map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <label style={{ fontSize: '12px', alignSelf: 'flex-end', paddingBottom: '6px' }}>
          <input type="checkbox" checked={inStockF} onChange={e => setInStockF(e.target.checked)} /> فقط دارای موجودی
        </label>
        <button onClick={load} style={{ ...smallBtn('#1a237e'), alignSelf: 'flex-end', marginBottom: '2px' }}>🔄 بروزرسانی</button>
      </div>

      {loading && <div style={ui.empty}>در حال بارگذاری...</div>}
      {err && <div style={ui.errBox}>⚠️ {err}</div>}

      {data && (
        <>
          {/* تب‌ها */}
          <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #ddd', marginBottom: '8px' }}>
            <button onClick={() => setTab('type')} style={tabStyle('type')}>به تفکیک نوع کالا ({toFa(typeTable?.rows?.length || 0)})</button>
            <button onClick={() => setTab('goods')} style={tabStyle('goods')}>به تفکیک کالا ({toFa(goodsTable?.rows?.length || 0)})</button>
          </div>

          {/* جدول نوع */}
          {tab === 'type' && typeTable && (
            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <table style={ui.table}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                  <tr>
                    <th style={ui.th}>ردیف</th>
                    {typeCols.map(c => <th key={c} style={ui.th}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {typeRowsWithTot.map((r, i) => {
                    const isTot = i === typeRowsWithTot.length - 1 && typeTot;
                    return (
                      <tr key={i} style={{ background: isTot ? '#e3f2fd' : (i % 2 ? '#fdf6ec' : '#fff'), fontWeight: isTot ? 800 : 400 }}>
                        <td style={ui.tdNum}>{isTot ? '' : toFa(i + 1)}</td>
                        {typeCols.map(c => {
                          const v = r[c];
                          const money = typeMoneyC.includes(c);
                          return <td key={c} style={isNum(v) ? ui.tdNum : ui.td}>
                            {isNum(v) ? (money ? fmtAmt(v) : toFa(v)) : (v ?? '')}
                          </td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {typeTable.rows.length === 0 && <div style={ui.empty}>اطلاعاتی یافت نشد</div>}
            </div>
          )}

          {/* جدول کالا */}
          {tab === 'goods' && goodsTable && (
            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <table style={ui.table}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                  <tr>
                    <th style={ui.th}>ردیف</th>
                    {goodsCols.map(c => <th key={c} style={ui.th}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(goodsTable.rows || []).map((r, i) => (
                    <tr key={i} style={{ background: i % 2 ? '#fdf6ec' : '#fff' }}>
                      <td style={ui.tdNum}>{toFa(i + 1)}</td>
                      {goodsCols.map(c => {
                        const v = r[c];
                        const money = goodsMoneyC.includes(c);
                        return <td key={c} style={isNum(v) ? ui.tdNum : ui.td}>
                          {isNum(v) ? (money ? fmtAmt(v) : toFa(v)) : (v ?? '')}
                        </td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {goodsTable.rows.length === 0 && <div style={ui.empty}>اطلاعاتی یافت نشد</div>}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════
   دیالوگ ارسال اطلاعات به خزانه (فقط variant=1)
   — بازه برچسب، شماره/تاریخ نامه، ذخیره/حذف نامه، حداقل قیمت
   — جدول خروجی Arrival با سرستون فارسی، اکسل خروجی، پیام خطا
   ══════════════════════════════════════════════════════════════════ */
const ARRIVAL_LABELS = {
  Kalauid: 'کد کالا', CurState: 'وضعیت فعلی', acqtyp: 'نوع تملک', anbrsdno: 'شماره قبض انبار',
  anbhvlno: 'شماره حواله', custuid: 'کد مشتری', lable: 'برچسب', amvuid: 'کد اموال',
  Namuid: 'شماره نامه', Year: 'سال', Desc: 'شرح', Cost: 'بهای تمام شده', transfer: 'انتقالی',
  Uid1: 'ویژگی ۱', Uid2: 'ویژگی ۲', Uid3: 'ویژگی ۳', Uid4: 'ویژگی ۴',
  Desc1: 'ویژگی ۵', Desc2: 'ویژگی ۶', Desc3: 'ویژگی ۷', Desc4: 'ویژگی ۸', couid: 'کد شرکت',
};
const ARRIVAL_MONEY = ['Cost'];

function KhazanehDialog({ prefix, onClose, onDone }) {
  const [init, setInit] = useState(null);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [sendNo, setSendNo] = useState('');
  const [sendDate, setSendDate] = useState('');
  const [saveLetter, setSaveLetter] = useState(true);
  const [unsaveLetter, setUnsaveLetter] = useState(false);
  const [count, setCount] = useState(null);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [notices, setNotices] = useState([]);
  const [ask, setAsk] = useState(null);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    API(`/${prefix}/khazaneh/init/`).then(j => {
      if (j.ok) { setInit(j); setStart(j.start || 0); setEnd(j.end || 0); setCount(j.count ?? null); }
    });
  }, [prefix]);

  const refreshCount = async () => {
    const j = await API(`/${prefix}/khazaneh/init/?${qs({ start, end })}`);
    if (j.ok) setCount(j.count ?? 0);
    else setErr(j.msg || 'خطا');
  };

  const validate = () => {
    if (!sendNo.trim()) { setErr('شماره نامه الزامی است'); return false; }
    if (!sendDate || sendDate.length !== 10) { setErr('تاریخ نامه نامعتبر است'); return false; }
    if (start <= 0 || end <= 0 || end < start) { setErr('بازه برچسب نامعتبر است'); return false; }
    return true;
  };

  const doSend = async (ans = {}) => {
    if (!validate()) return;
    setSaving(true); setErr(''); setNotices([]);
    const merged = { ...answers, ...ans };
    const j = await API(`/${prefix}/khazaneh/`, {
      method: 'POST',
      body: JSON.stringify({
        start, end, send_no: toEng(sendNo), send_date: toEng(sendDate),
        min_price: init?.min_price,
        save_letter: saveLetter ? 1 : 0,
        unsave_letter: unsaveLetter ? 1 : 0,
        answers: merged,
      }),
    });
    setSaving(false);
    if (j.ask) { setAsk(j.ask); return; }
    if (!j.ok) { setErr(j.msg || 'خطا'); return; }
    setResult(j);
    if (j.notices && j.notices.length) setNotices(j.notices);
    if (onDone) onDone();
  };

  // ستون‌ها و ردیف‌های جدول خروجی
  const resCols = result?.columns || [];
  const resRows = result?.rows || [];
  const exCols = resCols.map(c => ({ key: c, label: ARRIVAL_LABELS[c] || c, num: ARRIVAL_MONEY.includes(c) || resRows.some(r => isNum(r[c])) }));

  return (
    <Modal title="ارسال اطلاعات به خزانه" onClose={onClose} width="98vw"
      footer={<>
        <button onClick={onClose} style={ui.btnCancel}>بستن</button>
        {result && <ExcelButton title={`خروجی خزانه — ${toFa(start)} تا ${toFa(end)}`} columns={exCols} rows={resRows} />}
        {!result && <button onClick={() => doSend()} disabled={saving || !init}
          style={{ ...ui.btnSave, opacity: saving || !init ? 0.5 : 1 }}>{saving ? '⏳ در حال ارسال...' : '📤 ارسال به خزانه'}</button>}
      </>}>

      {/* پیام‌ها */}
      {err && <div style={ui.errBox}>⚠️ {err}</div>}
      {notices.length > 0 && <div style={{ ...ui.errBox, background: '#fff3e0', borderColor: '#ff9800' }}>{notices.map((n, i) => <div key={i}>{n}</div>)}</div>}

      {!result ? (
        <>
          {/* اطلاعات اولیه */}
          {init && (
            <div style={{ background: '#e8eaf6', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '12px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <span>حداقل قیمت اموال: <b>{fmtAmt(init.min_price)}</b> ریال</span>
              <span>اولین برچسب بدون نامه: <b>{toFa(init.start)}</b></span>
              <span>آخرین برچسب بدون نامه: <b>{toFa(init.end)}</b></span>
            </div>
          )}

          {/* بازه برچسب */}
          <div style={ui.row}>
            <Field label="از برچسب" width="130px">
              <TextIn num value={start} onChange={v => setStart(Number(toEng(v)) || 0)} />
            </Field>
            <Field label="تا برچسب" width="130px">
              <TextIn num value={end} onChange={v => setEnd(Number(toEng(v)) || 0)} />
            </Field>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
              <button onClick={refreshCount} style={smallBtn('#00695c')}>🔍 بررسی تعداد</button>
            </div>
            {count !== null && (
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '6px', fontSize: '13px', fontWeight: 700, color: count > 0 ? '#2e7d32' : '#c62828' }}>
                تعداد اقلام: {toFa(count)}
              </div>
            )}
          </div>

          {/* نامه */}
          <div style={ui.row}>
            <Field label="شماره نامه ارسال">
              <TextIn value={sendNo} onChange={setSendNo} placeholder="شماره نامه..." />
            </Field>
            <Field label="تاریخ نامه">
              <DateIn value={sendDate} onChange={setSendDate} />
            </Field>
          </div>

          {/* گزینه‌های ذخیره/حذف نامه */}
          <div style={{ display: 'flex', gap: '24px', marginTop: '8px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={saveLetter} onChange={e => { setSaveLetter(e.target.checked); if (e.target.checked) setUnsaveLetter(false); }} />
              <span>ذخیره شماره نامه در رکوردها</span>
              <span style={{ fontSize: '10px', color: '#666' }}>(شماره و تاریخ نامه در فیلد SendLetter ذخیره می‌شود)</span>
            </label>
            <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={unsaveLetter} onChange={e => { setUnsaveLetter(e.target.checked); if (e.target.checked) setSaveLetter(false); }} />
              <span>حذف نامه قبلی از رکوردها</span>
              <span style={{ fontSize: '10px', color: '#666' }}>(فیلدهای SendLetter خالی می‌شوند)</span>
            </label>
          </div>

          {/* هشدار عدم انتخاب */}
          {!saveLetter && !unsaveLetter && (
            <div style={{ fontSize: '11px', color: '#e65100', marginTop: '6px' }}>
              ⚠️ هیچ‌کدام از گزینه‌های ذخیره/حذف نامه انتخاب نشده — فقط خروجی تولید می‌شود بدون تغییر در رکوردها.
            </div>
          )}
        </>
      ) : (
        /* نتیجه ارسال */
        <>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '13px', flexWrap: 'wrap' }}>
            <span style={{ background: '#e8f5e9', padding: '6px 14px', borderRadius: '8px', fontWeight: 700 }}>
              ✅ تعداد ارسال‌شده: {toFa(result.count)}
            </span>
            <span>بازه: {toFa(start)} تا {toFa(end)}</span>
            {saveLetter && <span style={{ color: '#2e7d32' }}>📝 نامه ذخیره شد</span>}
            {unsaveLetter && <span style={{ color: '#c62828' }}>🗑️ نامه قبلی حذف شد</span>}
          </div>
          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <table style={ui.table}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                <tr>
                  <th style={ui.th}>ردیف</th>
                  {resCols.map(c => <th key={c} style={ui.th}>{ARRIVAL_LABELS[c] || c}</th>)}
                </tr>
              </thead>
              <tbody>
                {resRows.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 ? '#fdf6ec' : '#fff' }}>
                    <td style={ui.tdNum}>{toFa(i + 1)}</td>
                    {resCols.map(c => {
                      const v = r[c];
                      const money = ARRIVAL_MONEY.includes(c);
                      return <td key={c} style={isNum(v) ? ui.tdNum : ui.td}>
                        {isNum(v) ? (money ? fmtAmt(v) : toFa(v)) : (v ?? '')}
                      </td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {resRows.length === 0 && <div style={ui.empty}>رکوردی یافت نشد</div>}
          </div>
        </>
      )}

      {/* ask/answers */}
      {ask && (
        <WarningDialog title="تایید" lines={ask.lines || []} confirmLabel="بلی" cancelLabel="خیر"
          onConfirm={() => { const k = ask.key; setAsk(null); const a = { ...answers, [k]: 'yes' }; setAnswers(a); doSend(a); }}
          onCancel={() => { const k = ask.key; setAsk(null); setAnswers(prev => ({ ...prev, [k]: 'no' })); }} />
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════
   دیالوگ حذف گروهی
   ══════════════════════════════════════════════════════════════════ */
function DeleteAllDialog({ prefix, rows, onClose, onDone }) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ask, setAsk] = useState(null);
  const [answers, setAnswers] = useState({});

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const doDelete = async (ans = {}) => {
    setBusy(true); setErr('');
    const merged = { ...answers, ...ans };
    const j = await API(`/${prefix}/delete-all/`, {
      method: 'POST', body: JSON.stringify({ ids: selected, answers: merged }),
    });
    setBusy(false);
    if (j.ask) { setAsk(j.ask); return; }
    if (!j.ok) { setErr(j.msg || 'خطا'); return; }
    onDone(j.deleted || selected.length);
  };

  return (
    <Modal title="حذف گروهی" onClose={onClose} width="600px"
      footer={<>
        <button onClick={onClose} style={ui.btnCancel}>لغو</button>
        <button disabled={selected.length === 0 || busy} onClick={() => doDelete()} style={{ ...ui.btnDel, opacity: selected.length === 0 ? 0.4 : 1 }}>
          {busy ? '⏳ ...' : `حذف (${toFa(selected.length)})`}
        </button>
      </>}>
      {err && <div style={ui.errBox}>⚠️ {err}</div>}
      <div style={{ maxHeight: '400px', overflow: 'auto' }}>
        <table style={ui.table}>
          <thead><tr><th style={ui.th}>✓</th><th style={ui.th}>شماره قبض</th><th style={ui.th}>نام کالا</th><th style={ui.th}>برچسب</th></tr></thead>
          <tbody>
            {(rows || []).map((r, i) => {
              const id = r.PropertyId;
              const on = selected.includes(id);
              return (
                <tr key={id} style={{ background: on ? '#ffcdd2' : (i % 2 ? '#fdf6ec' : '#fff'), cursor: 'pointer' }}
                  onClick={() => toggle(id)}>
                  <td style={ui.td}><input type="checkbox" checked={on} readOnly /></td>
                  <td style={ui.tdNum}>{toFa(r['شماره قبض'])}</td>
                  <td style={ui.td}>{r['نام کالا']}</td>
                  <td style={ui.tdNum}>{toFa(r['برچسب اموال'])}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {ask && (
        <WarningDialog title="تایید" lines={ask.lines || []} confirmLabel="بلی" cancelLabel="خیر"
          onConfirm={() => { const k = ask.key; setAsk(null); const a = { ...answers, [k]: 'yes' }; setAnswers(a); doDelete(a); }}
          onCancel={() => { const k = ask.key; setAsk(null); setAnswers(a => ({ ...a, [k]: 'no' })); }} />
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════
   افزودن ویژگی جدید
   ══════════════════════════════════════════════════════════════════ */
function NewFeatureDialog({ prefix, goodsId, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    const j = await API(`/${prefix}/feature/create/`, { method: 'POST', body: JSON.stringify({ goods_id: goodsId, title }) });
    setBusy(false);
    if (j.ok) onCreated(j.id, j.title);
    else setErr(j.msg || 'خطا');
  };
  return (
    <Modal title="افزودن ویژگی" onClose={onClose} width="420px"
      footer={<>
        <button onClick={onClose} style={ui.btnCancel}>لغو</button>
        <button onClick={save} disabled={busy || !title.trim()} style={ui.btnSave}>{busy ? '⏳' : 'ثبت'}</button>
      </>}>
      {err && <div style={ui.errBox}>{err}</div>}
      <Field label="عنوان ویژگی">
        <TextIn value={title} onChange={setTitle} />
      </Field>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════
   دیالوگ گزارش فهرست اموال در محل — place_list report
   ══════════════════════════════════════════════════════════════════ */
function PlaceReportDialog({ prefix, variant, onClose }) {
  const [placeQ, setPlaceQ] = useState('');
  const [places, setPlaces] = useState([]);
  const [placeId, setPlaceId] = useState('');
  const [placeName, setPlaceName] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState('');

  // جستجوی اماکن — lookup endpoint
  const searchPlaces = async (q) => {
    setSearching(true);
    try {
      const res = await fetch(`/api/tadarokat/property/place/lookup/?q=${encodeURIComponent(q || '')}`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
      });
      const j = await res.json();
      setPlaces(j?.rows || j || []);
    } catch { setPlaces([]); }
    setSearching(false);
  };

  useEffect(() => { searchPlaces(''); }, []);

  const doReport = async () => {
    if (!placeId) { setErr('لطفاً محل را انتخاب کنید'); return; }
    setBusy(true); setErr('');
    try {
      const key = `tadarokat.property${variant === 2 ? '2' : ''}_place_list`;
      await openReport(key, { place_id: placeId, date });
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <Modal title="📍 گزارش فهرست اموال در محل" onClose={onClose} width="560px"
      footer={<>
        <button onClick={onClose} style={ui.btnCancel}>بستن</button>
        <button onClick={doReport} disabled={busy || !placeId} style={{ ...ui.btnSave, opacity: busy || !placeId ? 0.5 : 1 }}>
          {busy ? '⏳ ...' : '🖨️ چاپ گزارش'}
        </button>
      </>}>
      {err && <div style={ui.errBox}>⚠️ {err}</div>}

      {/* جستجو و انتخاب محل */}
      <Field label="جستجوی محل (اماکن)">
        <div style={{ display: 'flex', gap: '8px' }}>
          <TextIn value={placeQ} onChange={setPlaceQ} placeholder="نام محل را وارد کنید..."
            onKeyDown={e => e.key === 'Enter' && searchPlaces(placeQ)} />
          <button onClick={() => searchPlaces(placeQ)} style={smallBtn('#00695c')}>🔍</button>
        </div>
      </Field>

      {/* لیست اماکن */}
      {places.length > 0 && (
        <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '6px', marginTop: '8px' }}>
          <table style={ui.table}>
            <thead><tr><th style={ui.th}>کد</th><th style={ui.th}>نام محل</th><th style={ui.th}>انتخاب</th></tr></thead>
            <tbody>
              {places.map(p => {
                const pid = p.PlaceId || p.id;
                const name = p.PlaceName || p.label || p.name;
                const isSel = String(placeId) === String(pid);
                return (
                  <tr key={pid} style={{ background: isSel ? '#e3f2fd' : '#fff', cursor: 'pointer' }}
                    onClick={() => { setPlaceId(pid); setPlaceName(name); }}>
                    <td style={ui.tdNum}>{toFa(p.PlaceNo || pid)}</td>
                    <td style={ui.td}>{name}</td>
                    <td style={{ ...ui.td, textAlign: 'center' }}>{isSel ? '✅' : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {searching && <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>در حال جستجو...</div>}
      {placeId && <div style={{ fontSize: '12px', fontWeight: 700, color: '#2e7d32', marginTop: '8px' }}>محل انتخاب‌شده: {placeName}</div>}

      <div style={{ marginTop: '12px' }}>
        <Field label="تاریخ گزارش (اختیاری)">
          <DateIn value={date} onChange={setDate} />
        </Field>
      </div>
    </Modal>
  );
}


/* ══════════════════════════════════════════════════════════════════
   مدیریت محل اموال — PropertyPlaceDialog  (FRMPROPERTYPLACE)
   ══════════════════════════════════════════════════════════════════ */
function PropertyPlaceDialog({ pid, goodsName, onClose }) {
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState(null); // null | 'new' | 'edit'
  const [placeId, setPlaceId] = useState(0);
  const [placeName, setPlaceName] = useState('');
  const [permissionNo, setPermissionNo] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [ask, setAsk] = useState(null);
  const [answers, setAnswers] = useState({});
  const [logData, setLogData] = useState(null);

  const load = useCallback(() => {
    API(`/place/${pid}/`).then(j => {
      if (j.ok) { setCols(j.columns || []); setRows(j.data || []); }
      else setErr(j.msg || 'خطا');
    });
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  const selectRow = (r) => {
    setSel(r);
    setPlaceId(r.PlaceId || 0);
    setPlaceName(r['محل'] || '');
    setPermissionNo(r['مجوز'] || '');
    setEventDate(r['تاریخ'] || '');
    setQuantity(r['تعداد'] || 1);
  };

  const emptyForm = () => {
    setPlaceId(0); setPlaceName(''); setPermissionNo(''); setEventDate(''); setQuantity(1);
  };

  const startNew = () => { emptyForm(); setSel(null); setErr(''); setAnswers({}); setMode('new'); };
  const startEdit = () => { if (!sel) return; setErr(''); setAnswers({}); setMode('edit'); };
  const cancel = () => { setMode(null); if (sel) selectRow(sel); else emptyForm(); };

  const doSave = async (ans = {}) => {
    setSaving(true); setErr('');
    const merged = { ...answers, ...ans };
    const body = { place_id: placeId, permission_no: toEng(permissionNo), event_date: toEng(eventDate), quantity: Number(toEng(String(quantity))) || 1, answers: merged };
    const recId = sel?.PropertyPlaceId;
    const j = mode === 'new'
      ? await API(`/place/${pid}/create/`, { method: 'POST', body: JSON.stringify(body) })
      : await API(`/place/${pid}/${recId}/update/`, { method: 'PUT', body: JSON.stringify(body) });
    setSaving(false);
    if (j.ask) { setAsk(j.ask); return; }
    if (!j.ok) { setErr(j.msg || 'خطا'); return; }
    if (j.notices && j.notices.length) setMsg(j.notices.join('\n'));
    setMode(null); setAnswers({}); load();
  };

  const doDelete = async (ans = {}) => {
    if (!sel) return;
    const merged = { ...answers, ...ans };
    const j = await API(`/place/${pid}/${sel.PropertyPlaceId}/delete/`, {
      method: 'POST', body: JSON.stringify({ answers: merged }),
    });
    if (j.ask) { setAsk(j.ask); return; }
    if (!j.ok) { setMsg(j.msg || 'خطا'); return; }
    if (j.notices && j.notices.length) setMsg(j.notices.join('\n'));
    setSel(null); emptyForm(); setAnswers({}); load();
  };

  const showLog = () => {
    if (!sel) return;
    API(`/place/${pid}/${sel.PropertyPlaceId}/log/`).then(j => {
      if (j.ok) setLogData({ columns: j.columns || [], data: j.data || [] });
    });
  };

  // place lookup
  const [placeSearch, setPlaceSearch] = useState('');
  const [placeList, setPlaceList] = useState([]);
  const [placeOpen, setPlaceOpen] = useState(false);
  const placeTimer = useRef(null);

  const searchPlace = (q) => {
    setPlaceSearch(q); setPlaceName(q);
    if (placeTimer.current) clearTimeout(placeTimer.current);
    placeTimer.current = setTimeout(async () => {
      const j = await API(`/place/lookup/?q=${encodeURIComponent(q)}`);
      setPlaceList(j.ok ? j.data : []);
      setPlaceOpen(true);
    }, 300);
  };

  const pickPlace = (item) => {
    setPlaceId(item.id);
    setPlaceName(item.title || item.display || '');
    setPlaceSearch('');
    setPlaceOpen(false);
  };

  const editing = mode !== null;

  return (
    <>
      <Modal title={`مدیریت محل اموال${goodsName ? ` — ${goodsName}` : ''}`} onClose={onClose} width="860px"
        footer={<>
          <button onClick={onClose} style={ui.btnCancel}>بستن</button>
          {sel && !editing && <button onClick={showLog} style={smallBtn('#37474f')}>سابقه</button>}
        </>}>
        {err && <div style={ui.errBox}>⚠️ {err}</div>}

        {/* دکمه‌های عملیات */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
          {!editing && <button onClick={startNew} style={smallBtn('#2e7d32')}>＋ جدید</button>}
          {!editing && sel && <button onClick={startEdit} style={smallBtn('#1565c0')}>✎ ویرایش</button>}
          {!editing && sel && <button onClick={() => { setAnswers({}); doDelete(); }} style={smallBtn('#c62828')}>✕ حذف</button>}
          {editing && <button onClick={() => doSave()} disabled={saving} style={smallBtn('#2e7d32')}>{saving ? '⏳ ...' : 'ثبت'}</button>}
          {editing && <button onClick={cancel} style={smallBtn('#757575')}>انصراف</button>}
        </div>

        {/* فرم */}
        <div style={{ ...ui.row, marginBottom: '12px' }}>
          <Field label="محل" flex={2}>
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <input value={placeName} disabled={!editing} placeholder="جستجوی محل..."
                  onChange={e => searchPlace(e.target.value)}
                  onFocus={() => editing && placeName && searchPlace(placeName)}
                  onBlur={() => setTimeout(() => setPlaceOpen(false), 200)}
                  style={{ ...ui.input, background: !editing ? '#f3f3f3' : '#fff' }} />
                {editing && placeId > 0 && <button type="button" onClick={() => { setPlaceId(0); setPlaceName(''); }}
                  style={{ ...ui.btnCancel, padding: '4px 10px' }}>✕</button>}
              </div>
              {placeOpen && placeList.length > 0 && (
                <div style={{ position: 'absolute', zIndex: 50, background: '#fff', border: '1px solid #ccc', borderRadius: '8px',
                  maxHeight: '200px', overflowY: 'auto', width: '100%', boxShadow: '0 6px 18px rgba(0,0,0,0.15)' }}>
                  {placeList.map(o => (
                    <div key={o.id} onMouseDown={() => pickPlace(o)}
                      style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}>
                      {toFa(o.display || o.title)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Field>
          <Field label="تاریخ" width="140px">
            <DateIn value={eventDate} onChange={v => setEventDate(v)} disabled={!editing} />
          </Field>
          <Field label="شماره مجوز" width="120px">
            <TextIn value={permissionNo} onChange={v => setPermissionNo(v)} disabled={!editing} />
          </Field>
          <Field label="تعداد" width="80px">
            <TextIn num value={quantity} onChange={v => setQuantity(v)} disabled={!editing} />
          </Field>
        </div>

        {/* جدول */}
        <div style={{ maxHeight: '340px', overflow: 'auto' }}>
          <Grid columns={cols} rows={rows} rowKey="PropertyPlaceId" selectedKey={sel?.PropertyPlaceId}
            onSelect={r => { if (!editing) selectRow(r); }} empty="سابقه‌ای یافت نشد" />
        </div>
      </Modal>

      {/* لاگ تغییرات محل */}
      {logData && (
        <Modal title="سابقه تغییرات" onClose={() => setLogData(null)} width="700px"
          footer={<button onClick={() => setLogData(null)} style={ui.btnCancel}>بستن</button>}>
          <div style={{ maxHeight: '350px', overflow: 'auto' }}>
            <Grid columns={logData.columns} rows={logData.data} empty="سابقه‌ای یافت نشد" />
          </div>
        </Modal>
      )}

      {/* ask/answer */}
      {ask && (
        <WarningDialog title="تایید" lines={ask.lines || []} confirmLabel="بلی" cancelLabel="خیر"
          onConfirm={() => {
            const k = ask.key;
            setAsk(null);
            const a = { ...answers, [k]: 'yes' };
            setAnswers(a);
            if (mode === 'new' || mode === 'edit') doSave(a);
            else doDelete(a);
          }}
          onCancel={() => { const k = ask.key; setAsk(null); setAnswers(prev => ({ ...prev, [k]: 'no' })); }} />
      )}

      {msg && <MsgDialog text={msg} onClose={() => setMsg('')} />}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════
   دریافت اموال از اکسل — GetPropertyDialog  (FRMGETPROPERTY)
   ══════════════════════════════════════════════════════════════════ */
const TOOLS_BASE = '/api/tadarokat/property-tools';
const TAPI = (path, opts = {}) => fetch(`${TOOLS_BASE}${path}`, {
  headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);
const TAPI_JSON = (path, opts = {}) => fetch(`${TOOLS_BASE}${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

function GetPropertyDialog({ variant, onClose, onDone }) {
  const [init, setInit] = useState(null);
  const [step, setStep] = useState(0); // 0=select file, 1=map columns, 2=import, 3=done
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheet, setSheet] = useState('');
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [preset, setPreset] = useState(1);
  const [idx, setIdx] = useState({});
  const [serialNo, setSerialNo] = useState('');
  const [serialDate, setSerialDate] = useState('');
  const [serialExists, setSerialExists] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [delResult, setDelResult] = useState('');

  // init
  useEffect(() => {
    TAPI('/get/init/').then(j => {
      if (j.ok) {
        setInit(j);
        setPreset(j.preset || 1);
        applyPreset(j.presets, j.preset || 1, j.fields);
      }
    });
  }, []);

  const applyPreset = (presets, p, fields) => {
    if (!presets || !presets[p]) { setIdx({}); return; }
    const m = presets[p];
    const newIdx = {};
    (fields || []).forEach(f => { newIdx[f.key] = m[f.key] || 0; });
    setIdx(newIdx);
  };

  // upload file
  const uploadFile = async (f, sh) => {
    if (!f) return;
    setErr('');
    const fd = new FormData();
    fd.append('file', f);
    if (sh) fd.append('sheet', sh);
    const j = await fetch(`${TOOLS_BASE}/get/sheet/`, {
      method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('access')}` }, body: fd,
    }).then(jsonOf, netErr);
    if (!j.ok) { setErr(j.msg || 'خطای خواندن فایل'); return; }
    setSheets(j.sheets || []);
    setSheet(j.sheet || '');
    setColumns(j.columns || []);
    setData(j.data || []);
    setStep(1);
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    uploadFile(f);
  };

  const changeSheet = (sh) => {
    setSheet(sh);
    if (file) uploadFile(file, sh);
  };

  // check serial
  const checkSerial = async () => {
    if (!serialNo || serialDate.length !== 10) { setSerialExists(false); return; }
    const j = await TAPI(`/get/serial-check/?serial_no=${encodeURIComponent(toEng(serialNo))}&serial_date=${encodeURIComponent(toEng(serialDate))}`);
    setSerialExists(j.ok && j.exists);
  };

  // import
  const doImport = async () => {
    if (!serialNo || serialDate.length !== 10) { setErr('شماره و تاریخ سریال الزامی است'); return; }
    setImporting(true); setErr('');
    const body = {
      serial_no: toEng(serialNo), serial_date: toEng(serialDate),
      indexes: Object.fromEntries(Object.entries(idx).map(([k, v]) => [k, Number(v) || 0])),
      rows: data,
    };
    const j = await TAPI_JSON('/get/apply/', { method: 'POST', body: JSON.stringify(body) });
    setImporting(false);
    if (!j.ok) { setErr(j.msg || 'خطا'); return; }
    setResult(j);
    setStep(3);
    if (onDone) onDone();
  };

  // delete by serial
  const doDeleteBySerial = async () => {
    if (!serialNo || serialDate.length !== 10) { setErr('شماره و تاریخ سریال الزامی است'); return; }
    setDeleting(true); setErr('');
    const j = await TAPI_JSON('/get/delete/', {
      method: 'POST', body: JSON.stringify({ serial_no: toEng(serialNo), serial_date: toEng(serialDate) }),
    });
    setDeleting(false);
    if (!j.ok) { setErr(j.msg || 'خطا'); return; }
    setDelResult(`${toFa(j.deleted || 0)} رکورد حذف شد`);
  };

  const setFieldIdx = (key, val) => setIdx(prev => ({ ...prev, [key]: Number(val) || 0 }));
  const colOpts = columns.map((c, i) => ({ v: i + 1, l: `${i + 1}: ${c}` }));

  if (!init) return <Modal title="دریافت اموال" onClose={onClose}><div style={{ padding: '20px', textAlign: 'center' }}>در حال بارگذاری...</div></Modal>;

  return (
    <Modal title="دریافت اموال از فایل اکسل" onClose={onClose} width="900px"
      footer={<>
        <button onClick={onClose} style={ui.btnCancel}>بستن</button>
        {step === 1 && <button onClick={() => setStep(2)} style={smallBtn('#1565c0')}>مرحله بعد</button>}
        {step === 2 && <button onClick={doImport} disabled={importing} style={smallBtn('#2e7d32')}>{importing ? '⏳ در حال واردسازی...' : '📥 واردسازی'}</button>}
      </>}>
      {err && <div style={ui.errBox}>⚠️ {err}</div>}

      {/* مرحله ۰: انتخاب فایل */}
      {step === 0 && (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '16px' }}>فایل اکسل (.xlsx) را انتخاب کنید</div>
          <input type="file" accept=".xlsx" onChange={handleFile}
            style={{ fontSize: '13px', padding: '10px', border: '2px dashed #aaa', borderRadius: '8px', cursor: 'pointer' }} />
        </div>
      )}

      {/* مرحله ۱: نگاشت ستون‌ها */}
      {step === 1 && (
        <>
          <div style={{ ...ui.row, marginBottom: '12px', alignItems: 'flex-end' }}>
            {sheets.length > 1 && (
              <Field label="شیت" width="180px">
                <select value={sheet} onChange={e => changeSheet(e.target.value)} style={ui.input}>
                  {sheets.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            )}
            <Field label="تنظیمات پیش‌فرض" width="180px">
              <select value={preset} onChange={e => { const p = Number(e.target.value); setPreset(p); applyPreset(init.presets, p, init.fields); }} style={ui.input}>
                {(init.preset_titles || []).map((t, i) => <option key={i} value={i}>{t}</option>)}
              </select>
            </Field>
            <div style={{ fontSize: '12px', color: '#555' }}>{toFa(data.length)} ردیف · {toFa(columns.length)} ستون</div>
          </div>
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table style={ui.table}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                <tr>
                  <th style={ui.th}>فیلد</th>
                  <th style={ui.th}>ستون اکسل</th>
                  <th style={ui.th}>نمونه</th>
                </tr>
              </thead>
              <tbody>
                {(init.fields || []).map(f => {
                  const ci = (idx[f.key] || 0) - 1;
                  const sample = ci >= 0 && data.length > 0 ? (data[0][ci] || '') : '';
                  return (
                    <tr key={f.key} style={{ background: ci >= 0 ? '#e8f5e9' : '#fff' }}>
                      <td style={ui.td}>{f.label}</td>
                      <td style={ui.td}>
                        <select value={idx[f.key] || 0} onChange={e => setFieldIdx(f.key, e.target.value)} style={{ ...ui.input, fontSize: '11px', padding: '3px 6px' }}>
                          <option value={0}>—</option>
                          {colOpts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </td>
                      <td style={{ ...ui.td, fontSize: '11px', color: '#666', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sample}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* مرحله ۲: سریال و تایید */}
      {step === 2 && (
        <div style={{ padding: '10px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '16px' }}>تنظیمات واردسازی</div>
          <div style={{ ...ui.row, marginBottom: '16px' }}>
            <Field label="شماره سریال" width="150px">
              <TextIn value={serialNo} onChange={v => setSerialNo(v)} onBlur={checkSerial} />
            </Field>
            <Field label="تاریخ سریال" width="150px">
              <DateIn value={serialDate} onChange={v => { setSerialDate(v); }} />
            </Field>
            {serialExists && <div style={{ color: '#c62828', fontSize: '12px', fontWeight: 700, alignSelf: 'flex-end', paddingBottom: '6px' }}>⚠️ این سریال قبلاً ثبت شده</div>}
          </div>
          <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>خلاصه:</div>
            <div style={{ fontSize: '12px' }}>تعداد ردیف: {toFa(data.length)}</div>
            <div style={{ fontSize: '12px' }}>فیلدهای نگاشت‌شده: {toFa(Object.values(idx).filter(v => v > 0).length)}</div>
          </div>

          <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '14px', marginTop: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', color: '#c62828' }}>حذف بر اساس سریال</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={doDeleteBySerial} disabled={deleting || !serialNo || serialDate.length !== 10}
                style={{ ...smallBtn('#c62828'), opacity: !serialNo || serialDate.length !== 10 ? 0.4 : 1 }}>{deleting ? '⏳ ...' : '🗑 حذف'}</button>
              {delResult && <span style={{ fontSize: '12px', color: '#2e7d32' }}>{delResult}</span>}
            </div>
          </div>
        </div>
      )}

      {/* مرحله ۳: نتیجه */}
      {step === 3 && result && (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
          <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>واردسازی با موفقیت انجام شد</div>
          <div style={{ fontSize: '13px', color: '#555' }}>
            ثبت شده: {toFa(result.added || 0)} &nbsp;|&nbsp; به‌روزرسانی شده: {toFa(result.updated || 0)}
          </div>
        </div>
      )}
    </Modal>
  );
}


/* ══════════════════════════════════════════════════════════════════
   صفحه اصلی: PropertyPage
   ══════════════════════════════════════════════════════════════════ */
const BUTTONS_V1 = [
  'BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNDELETEALL', 'BTNOK', 'BTNCANCEL',
  'BTNGOODSREQUEST', 'BTNHISTORY', 'BTNSENDTOKHAZANEH', 'BTNPROPERTYALLREPORTS', 'BTNDEPRECIATION',
  'BTNSTOCKSLIP', 'BTNPROPERTYPLACEREPORT', 'BTNPROPERTYPLACE', 'BTNGETPROPERTY', 'BTNHELP',
];
const BUTTONS_V2 = [
  'BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNDELETEALL', 'BTNOK', 'BTNCANCEL',
  'BTNGOODSREQUEST', 'BTNHISTORY', 'BTNPROPERTYALLREPORTS',
  'BTNSTOCKSLIP', 'BTNPROPERTYPLACEREPORT', 'BTNPROPERTYPLACE', 'BTNGETPROPERTY', 'BTNHELP',
];

export default function PropertyPage({ user, onBack, variant = 1, preset }) {
  const v = variant;
  const prefix = String(v);  // '1' or '2'
  const formName = v === 1 ? 'FRMPROPERTY' : 'FRMPROPERTY2';
  const BUTTONS = v === 1 ? BUTTONS_V1 : BUTTONS_V2;

  const fb = useFormButtons(formName, BUTTONS);
  const summaryKey = v === 1 ? 'PropertyChkSummary' : 'Property2ChkSummary';
  const [summary, setSummary] = useState(readSummary(summaryKey));
  const [year, setYear] = useState(curYear());
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [ptype, setPtype] = useState(0);
  const [inStock, setInStock] = useState(false);
  const [labelOnly, setLabelOnly] = useState(false);
  const [slipNo, setSlipNo] = useState('');
  const [slipYear, setSlipYear] = useState('');
  const [cols, setCols] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [modal, setModal] = useState(null); // 'new' | 'edit' | 'delete' | 'deleteAll' | 'requests' | 'history' | 'log' | 'khazaneh' | 'depreciation' | 'newFeature' | 'propertyPlace' | 'getProperty'
  const [form, setForm] = useState(EMPTY());
  const [goodsLocked, setGoodsLocked] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [ask, setAsk] = useState(null);
  const [answers, setAnswers] = useState({});
  const [initData, setInitData] = useState(null);
  const [slipSum, setSlipSum] = useState(null);

  // init
  useEffect(() => {
    API(`/${prefix}/init/`, { method: 'POST', body: '{}' }).then(j => {
      if (j.ok) {
        setInitData(j);
        if (j.year) setYear(j.year);
      }
    });
  }, [prefix]);

  // list
  const L = usePagedList((page, o) => {
    const q = {
      page, year: o.year, summary: o.summary ? 1 : 0, search: o.search,
      filter: o.filter, ptype: o.ptype || undefined, in_stock: o.inStock ? 1 : undefined,
      label_only: o.labelOnly ? 1 : undefined, slip_no: o.slipNo || undefined, slip_year: o.slipYear || undefined,
    };
    return API(`/${prefix}/list/?${qs(q)}`).then(j => {
      if (j.ok && page === 1) setCols(j.columns || []);
      if (j.year) setYear(j.year);
      return j;
    });
  });

  const reload = (over = {}) => L.reload({ summary, year, search, filter, ptype, inStock, labelOnly, slipNo, slipYear, ...over });
  useEffect(() => { if (fb.open) reload(); }, [fb.open]);

  // preset (navigation from Place page)
  useEffect(() => {
    if (preset && preset.propertyId && fb.open) {
      // load detail for preset propertyId
      API(`/${prefix}/${preset.propertyId}/detail/`).then(j => {
        if (j.ok) setDetail(j);
      });
    }
  }, [preset, fb.open, prefix]);

  // detail on select
  useEffect(() => {
    setDetail(null);
    if (!sel || summary) return;
    const pid = sel.PropertyId;
    if (!pid) return;
    const chkStock = initData?.chk_stock ? 1 : 0;
    API(`/${prefix}/${pid}/detail/?stock=${chkStock}`).then(j => { if (j.ok) setDetail(j); });
  }, [sel, summary, prefix, initData]);

  /* ── helpers ── */
  const F = (k, val, extra) => setForm(f => ({ ...f, [k]: val, ...(extra || {}) }));
  const payload = () => ({
    ...form, year,
    stock_slip_date: toEng(form.stock_slip_date),
    send_letter_date: toEng(form.send_letter_date),
    factor_date: toEng(form.factor_date),
    first_use_date: toEng(form.first_use_date),
    answers,
  });

  /* ── defaults for new ── */
  const loadDefaults = async () => {
    const j = await API(`/${prefix}/new-defaults/`);
    if (j.ok) {
      const f = EMPTY();
      f.label_no = j.label_no || 0;
      if (initData?.today) f.stock_slip_date = initData.today;
      setForm(f);
    }
  };

  /* ── new slip ── */
  const newSlip = async (ans = {}) => {
    const merged = { ...answers, ...ans };
    const j = await API(`/${prefix}/new-slip/`, { method: 'POST', body: JSON.stringify({ year, answers: merged }) });
    if (j.ask) { setAsk(j.ask); return; }
    if (j.ok) {
      setForm(f => ({ ...f, stock_slip_no: j.stock_slip_no, stock_slip_date: j.stock_slip_date }));
      if (j.year) setYear(j.year);
    }
  };

  /* ── save (create / update) ── */
  const save = async (ans = {}) => {
    setSaving(true); setErr('');
    const merged = { ...answers, ...ans };
    const body = { ...payload(), answers: merged };
    const pid = sel?.PropertyId;
    const j = modal === 'new'
      ? await API(`/${prefix}/create/`, { method: 'POST', body: JSON.stringify(body) })
      : await API(`/${prefix}/${pid}/update/`, { method: 'PUT', body: JSON.stringify(body) });
    setSaving(false);
    if (j.ask) { setAsk(j.ask); return; }
    if (!j.ok) { setErr(j.msg || 'خطا'); return; }
    if (j.notices && j.notices.length) setMsg(j.notices.join('\n'));
    setModal(null); setAnswers({}); reload();
    if (j.slip_sum !== undefined) setSlipSum(j.slip_sum);
    // update defaults for next record
    if (modal === 'new' && j.label_no) setForm(f => ({ ...f, label_no: j.label_no }));
  };

  /* ── edit check ── */
  const editCheck = async () => {
    if (!sel) return;
    const j = await API(`/${prefix}/${sel.PropertyId}/edit-check/`);
    if (!j.ok) { setMsg(j.msg || 'خطا'); return; }
    if (j.notices && j.notices.length) {
      setMsg(j.notices.join('\n'));
      return;
    }
    setGoodsLocked(j.goods_locked || false);
    setForm(rowToForm(sel));
    setErr(''); setAnswers({});
    setModal('edit');
  };

  /* ── delete single ── */
  const doDelete = async (ans = {}) => {
    const merged = { ...answers, ...ans };
    const j = await API(`/${prefix}/${sel.PropertyId}/delete/`, {
      method: 'POST', body: JSON.stringify({ answers: merged }),
    });
    if (j.ask) { setAsk(j.ask); return; }
    if (!j.ok) { setMsg(j.msg || 'خطا'); return; }
    if (j.notices && j.notices.length) setMsg(j.notices.join('\n'));
    setModal(null); setSel(null); setAnswers({}); reload();
  };

  /* ── add from requests ── */
  const addRequests = async (keys, mode) => {
    const body = { ...payload(), keys, mode, year, answers: {} };
    const j = await API(`/${prefix}/requests/add/`, { method: 'POST', body: JSON.stringify(body) });
    if (j.ask) { setAsk(j.ask); return; }
    if (!j.ok) { setMsg(j.msg || 'خطا'); return; }
    if (j.notices && j.notices.length) setMsg(j.notices.join('\n'));
    setModal(null); reload();
  };

  /* ── report ── */
  const report = async (key, params = {}) => {
    try { await openReport(key, params); } catch (e) { setMsg(e.message); }
  };

  /* ── UI ── */
  const has = !!sel && fb.open;
  const btn = (code, label, onClick, needSel = true, bg) => fb.can(code) && (
    <button key={code} onClick={onClick} disabled={needSel && !sel}
      style={{ ...smallBtn(bg), opacity: needSel && !sel ? 0.4 : 1 }}>{label}</button>
  );

  const toolbar = fb.open ? (
    <>
      <div style={ui.toolBtns}>
        {fb.can('BTNNEW') && <button onClick={() => { loadDefaults(); setGoodsLocked(false); setErr(''); setAnswers({}); setModal('new'); }} style={ui.btnNew}>＋ جدید</button>}
        {fb.can('BTNNEW') && <button onClick={() => newSlip()} style={smallBtn('#00695c')}>قبض جدید</button>}
        {fb.can('BTNEDIT') && <button disabled={!has} onClick={editCheck} style={{ ...ui.btnEdit, opacity: has ? 1 : 0.4 }}>✎ ویرایش</button>}
        {fb.can('BTNDELETE') && <button disabled={!has} onClick={() => { setAnswers({}); setModal('delete'); }} style={{ ...ui.btnDel, opacity: has ? 1 : 0.4 }}>✕ حذف</button>}
        {fb.can('BTNDELETEALL') && <button onClick={() => setModal('deleteAll')} disabled={L.data.length === 0}
          style={{ ...smallBtn('#b71c1c'), opacity: L.data.length ? 1 : 0.4 }}>حذف گروهی</button>}
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
        {v === 1 && (
          <label style={{ fontSize: '12px', alignSelf: 'center' }}>
            <input type="checkbox" checked={summary} onChange={e => { const c = e.target.checked; setSummary(c); saveSummary(summaryKey, c); setSel(null); reload({ summary: c }); }} /> خلاصه
          </label>
        )}
        <Field label="سال مالی" width="80px">
          <input value={year} onChange={e => setYear(Number(e.target.value) || '')} onKeyDown={e => e.key === 'Enter' && reload()} style={ui.yearInput} />
        </Field>
        {v === 1 && (
          <Field label="فیلتر" width="160px">
            <select value={filter} onChange={e => { setFilter(e.target.value); reload({ filter: e.target.value }); }} style={ui.input}>
              {FILTERS_V1.map(f => <option key={f.v} value={f.v}>{f.l}</option>)}
            </select>
          </Field>
        )}
        <Field label="نوع مال" width="160px">
          <select value={ptype} onChange={e => { setPtype(Number(e.target.value)); reload({ ptype: Number(e.target.value) }); }} style={ui.input}>
            <option value={0}>همه</option>
            {TYPE_ITEMS.map(o => <option key={o.v} value={o.v + 1}>{o.l}</option>)}
          </select>
        </Field>
        <label style={{ fontSize: '12px' }}>
          <input type="checkbox" checked={inStock} onChange={e => { setInStock(e.target.checked); reload({ inStock: e.target.checked }); }} /> دارای موجودی
        </label>
        <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && reload()} placeholder="جستجو" style={{ ...ui.searchInput, minWidth: '140px' }} />
        <label style={{ fontSize: '11px' }}>
          <input type="checkbox" checked={labelOnly} onChange={e => { setLabelOnly(e.target.checked); reload({ labelOnly: e.target.checked }); }} /> فقط برچسب
        </label>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
        <Field label="شماره قبض" width="100px">
          <TextIn num value={slipNo} onChange={v2 => setSlipNo(v2)} onKeyDown={e => e.key === 'Enter' && reload()} />
        </Field>
        <Field label="سال قبض" width="80px">
          <TextIn num value={slipYear} onChange={v2 => setSlipYear(v2)} onKeyDown={e => e.key === 'Enter' && reload()} />
        </Field>
        {btn('BTNGOODSREQUEST', 'درخواست‌ها', () => setModal('requests'), false, '#6a1b9a')}
        {btn('BTNHISTORY', 'سابقه', () => setModal('history'), true, '#4e342e')}
        {has && <button onClick={() => setModal('log')} style={smallBtn('#37474f')}>لاگ</button>}
        {v === 1 && btn('BTNSENDTOKHAZANEH', 'ارسال به خزانه', () => setModal('khazaneh'), false, '#01579b')}
        {v === 1 && btn('BTNDEPRECIATION', 'استهلاک', () => setModal('depreciation'), false, '#880e4f')}
        {btn('BTNSTOCKSLIP', '🧾 قبض انبار', () => report(`tadarokat.property${v === 2 ? '2' : ''}_stock_slip`, { id: sel?.PropertyId }), true, '#1b5e20')}
        {btn('BTNPROPERTYPLACEREPORT', '📍 گزارش محل', () => setModal('placeReport'), false, '#00695c')}
        {btn('BTNPROPERTYPLACE', '📦 مدیریت محل', () => setModal('propertyPlace'), true, '#4527a0')}
        {btn('BTNGETPROPERTY', '📥 دریافت اموال', () => setModal('getProperty'), false, '#006064')}
        {btn('BTNPROPERTYALLREPORTS', 'گزارش کلی', () => report(`tadarokat.property${v === 2 ? '2' : ''}_all`), false, '#e65100')}
        {btn('BTNHELP', '❓ راهنما', () => setModal('help'), false, '#455a64')}
        <CountBadge shown={L.data.length} total={L.pager.total} loading={L.loading} unit="قلم" />
        <ExcelButton title={v === 1 ? 'اموال و کالاهای سرمایه‌ای' : 'کالاهای مصرفی'} columns={excelCols(cols, L.data)} rows={L.data} />
      </div>
    </>
  ) : null;

  const moneyCols = ['قیمت', 'مالیات بر ارزش افزوده', 'تخفیف', 'استهلاک سالانه', 'استهلاک سالهای قبل', 'استهلاک سال جاری', 'جمع استهلاک'];

  const grid = (
    <Grid columns={cols} rows={L.data} rowKey="PropertyId" selectedKey={sel?.PropertyId} onSelect={setSel}
      moneyCols={moneyCols} empty={fb.loaded && !fb.open ? '' : 'رکوردی یافت نشد'} />
  );

  const pageTitle = v === 1 ? 'اموال و کالاهای سرمایه‌ای' : 'کالاهای مصرفی';

  return (
    <>
      <ListPage user={user} onBack={onBack} title={pageTitle} subtitle="تدارکات — سامانه ارجان"
        toolbar={toolbar} error={L.error} pager={L.pager} onMore={L.more} loading={L.loading} unit="قلم">
        {!summary && detail ? (
          <div style={ui.splitWrap}>
            <div style={ui.topPane}>{grid}</div>
            <div style={ui.bottomPane}>
              <DetailPanel data={detail} variant={v} />
            </div>
          </div>
        ) : grid}
      </ListPage>

      {/* مودال ثبت/ویرایش */}
      {(modal === 'new' || modal === 'edit') && (
        <Modal title={modal === 'new' ? '＋ ثبت جدید' : '✎ ویرایش'} onClose={() => { setModal(null); setAnswers({}); }}
          footer={<>
            <button onClick={() => { setModal(null); setAnswers({}); }} style={ui.btnCancel}>لغو</button>
            {slipSum !== null && <span style={{ fontSize: '12px', color: '#555' }}>جمع قبض: {fmtAmt(slipSum)}</span>}
            {fb.can('BTNOK') && fb.can(modal === 'new' ? 'BTNNEW' : 'BTNEDIT') &&
              <button onClick={() => save()} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : 'ثبت'}</button>}
          </>}>
          {err && <div style={ui.errBox}>⚠️ {err}</div>}
          <PropertyForm form={form} setF={F} variant={v} prefix={prefix} goodsLocked={goodsLocked}
            newFeature={() => setModal('newFeature')} />
        </Modal>
      )}

      {/* حذف تکی */}
      {modal === 'delete' && <DeleteDialog onConfirm={() => doDelete()} onCancel={() => setModal(null)} />}

      {/* حذف گروهی */}
      {modal === 'deleteAll' && (
        <DeleteAllDialog prefix={prefix} rows={L.data} onClose={() => setModal(null)}
          onDone={(n) => { setModal(null); setSel(null); reload(); setMsg(`${toFa(n)} قلم حذف شد`); }} />
      )}

      {/* درخواست‌ها */}
      {modal === 'requests' && (
        <RequestsDialog prefix={prefix} variant={v} onClose={() => setModal(null)}
          onAdd={(keys, mode) => addRequests(keys, mode)} />
      )}

      {/* سابقه واگذاری */}
      {modal === 'history' && sel && (
        <HistoryDialog prefix={prefix} pid={sel.PropertyId} onClose={() => setModal(null)} />
      )}

      {/* لاگ تغییرات */}
      {modal === 'log' && sel && (
        <LogDialog prefix={prefix} pid={sel.PropertyId} onClose={() => setModal(null)} />
      )}

      {/* ارسال به خزانه */}
      {modal === 'khazaneh' && (
        <KhazanehDialog prefix={prefix} onClose={() => setModal(null)} />
      )}

      {/* استهلاک */}
      {modal === 'depreciation' && (
        <DepreciationDialog prefix={prefix} year={year} onClose={() => setModal(null)} />
      )}

      {/* گزارش فهرست اموال در محل */}
      {modal === 'placeReport' && (
        <PlaceReportDialog prefix={prefix} variant={v} onClose={() => setModal(null)} />
      )}

      {/* مدیریت محل اموال */}
      {modal === 'propertyPlace' && sel && (
        <PropertyPlaceDialog pid={sel.PropertyId} goodsName={sel['نام کالا'] || ''} onClose={() => setModal(null)} />
      )}

      {/* دریافت اموال */}
      {modal === 'getProperty' && (
        <GetPropertyDialog variant={v} onClose={() => setModal(null)} onDone={() => reload()} />
      )}

      {/* راهنما */}
      {modal === 'help' && <HelpDialog variant={v} onClose={() => setModal(null)} />}

      {/* افزودن ویژگی */}
      {modal === 'newFeature' && (
        <NewFeatureDialog prefix={prefix} goodsId={form.goods_id}
          onClose={() => setModal(modal === 'newFeature' ? (form.goods_id ? 'new' : null) : modal)}
          onCreated={(id, t) => { F('feature_id', id, { feature_title: t }); setModal(form.ownership_id ? 'new' : 'edit'); }} />
      )}

      {/* پرسش‌های تراکنش (ask/answers) */}
      {ask && (
        <WarningDialog title="تایید" lines={ask.lines || []} confirmLabel="بلی" cancelLabel="خیر"
          onConfirm={() => {
            const k = ask.key;
            setAsk(null);
            const a = { ...answers, [k]: 'yes' };
            setAnswers(a);
            // re-send the last operation
            if (modal === 'new' || modal === 'edit') save(a);
            else if (modal === 'delete') doDelete(a);
            else if (modal === 'newSlip') newSlip(a);
          }}
          onCancel={() => {
            const k = ask.key;
            setAsk(null);
            setAnswers(a => ({ ...a, [k]: 'no' }));
          }} />
      )}

      {/* پیام */}
      {msg && <MsgDialog text={msg} onClose={() => setMsg('')} />}
    </>
  );
}
